import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';

const EDUCATION_MAX_BYTES = 200 * 1024 * 1024;
const DEVICE_IMAGE_MAX_BYTES = 6 * 1024 * 1024;
const CLIENT_LOGO_MAX_BYTES = 6 * 1024 * 1024;
const PROFILE_PHOTO_MAX_BYTES = 6 * 1024 * 1024;

const EDUCATION_ALLOWED = new Set([
  'application/pdf',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'audio/mpeg',
  'audio/mp4',
  'audio/webm',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

const DEVICE_IMAGE_ALLOWED = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);

/** @param {string|undefined|null} tenantId */
function normalizeTenantId(tenantId) {
  const t = String(tenantId || 'trilogy').trim();
  return t || 'trilogy';
}

/** Phase 5: all tenant-owned objects live under `tenants/{tenantId}/…`. */
export function clientLogoStoragePath(clientId, tenantId) {
  return `tenants/${normalizeTenantId(tenantId)}/clients/${String(clientId || '').trim()}/logo`;
}

function assertStorage() {
  if (!storage) {
    throw new Error(
      'Firebase Storage is not configured. Set REACT_APP_FIREBASE_STORAGE_BUCKET in the Care app environment.',
    );
  }
}

function safeFileName(name) {
  const base = String(name || 'file').split(/[/\\]/).pop() || 'file';
  return base.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 180) || 'file';
}

/**
 * Upload education media (PDF, video, audio, images). Returns HTTPS download URL for Firestore `link`.
 * @param {File} file
 * @param {string} educationId - Firestore educationLibrary document id
 * @param {string} tenantId - owning tenant (see `tenantIdForCareContentWrites`)
 */
export async function uploadEducationLibraryMedia(file, educationId, tenantId) {
  assertStorage();
  if (!file || !educationId) throw new Error('File and education id are required.');
  if (file.size > EDUCATION_MAX_BYTES) {
    throw new Error(`File is too large (max ${EDUCATION_MAX_BYTES / (1024 * 1024)} MB).`);
  }
  const type = (file.type || '').toLowerCase();
  if (type && !EDUCATION_ALLOWED.has(type)) {
    throw new Error(
      'Unsupported file type. Use PDF, common video (mp4, webm, mov), audio (mp3, m4a, webm), or an image (jpeg, png, gif, webp).',
    );
  }
  const tid = normalizeTenantId(tenantId);
  const path = `tenants/${tid}/education/${educationId}/${Date.now()}_${safeFileName(file.name)}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: type || 'application/octet-stream' });
  return getDownloadURL(storageRef);
}

/**
 * Upload a device setup reference image. Returns HTTPS download URL for Firestore `setupImageUrl`.
 * Shared catalog: use tenant `trilogy` for global device definitions (see Phase 5).
 * @param {File} file
 * @param {string} setupId - deviceSetups document id
 * @param {string} tenantId
 */
export async function uploadDeviceSetupImage(file, setupId, tenantId) {
  assertStorage();
  if (!file || !setupId) throw new Error('File and device setup id are required.');
  if (file.size > DEVICE_IMAGE_MAX_BYTES) {
    throw new Error(`Image is too large (max ${DEVICE_IMAGE_MAX_BYTES / (1024 * 1024)} MB).`);
  }
  const type = (file.type || '').toLowerCase();
  if (!DEVICE_IMAGE_ALLOWED.has(type)) {
    throw new Error('Use a JPEG, PNG, GIF, or WebP image.');
  }
  const ext = type === 'image/jpeg' ? '.jpg' : type === 'image/png' ? '.png' : type === 'image/gif' ? '.gif' : '.webp';
  const tid = normalizeTenantId(tenantId);
  const path = `tenants/${tid}/device-setups/${setupId}/image_${Date.now()}${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: type });
  return getDownloadURL(storageRef);
}

/**
 * Upload client branding logo (Firestore `clients.logoUrl` = download URL for patient app).
 * @param {File} file
 * @param {string} clientId - Firestore clients document id
 * @param {string} tenantId - must match Firestore `clients` doc `tenantId` for this client
 */
export async function uploadClientLogo(file, clientId, tenantId) {
  assertStorage();
  if (!file || !clientId) throw new Error('File and client id are required.');
  if (file.size > CLIENT_LOGO_MAX_BYTES) {
    throw new Error(`Image is too large (max ${CLIENT_LOGO_MAX_BYTES / (1024 * 1024)} MB).`);
  }
  const type = (file.type || '').toLowerCase();
  if (!DEVICE_IMAGE_ALLOWED.has(type)) {
    throw new Error('Use a JPEG, PNG, GIF, or WebP image.');
  }
  const path = clientLogoStoragePath(clientId, tenantId);
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: type });
  return getDownloadURL(storageRef);
}

/**
 * Remove stored logo for a client (best-effort; ignores missing object).
 * @param {string} clientId
 * @param {string} [tenantId] - defaults to trilogy when omitted
 */
export async function deleteClientLogoStorage(clientId, tenantId) {
  if (!storage || !clientId) return;
  const p = clientLogoStoragePath(clientId, tenantId);
  try {
    await deleteObject(ref(storage, p));
  } catch {
    /* not found or permission */
  }
}

// ---------------------------------------------------------------------------
// Profile photos (users & patients)
// ---------------------------------------------------------------------------

const PROFILE_PHOTO_MAX_DIMENSION = 600;
const PROFILE_PHOTO_JPEG_QUALITY = 0.82;
const PROFILE_PHOTO_ALLOWED = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
]);
// Sanity cap on the raw picked file; the real limit applies to the processed output.
const RAW_IMAGE_INPUT_MAX_BYTES = 25 * 1024 * 1024;

const HEIC_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);

/** iPhone photos are often HEIC, which canvas cannot decode. Browsers may report an empty MIME type for them. */
function isHeicFile(file) {
  const type = (file.type || '').toLowerCase();
  if (HEIC_TYPES.has(type)) return true;
  const name = (file.name || '').toLowerCase();
  return name.endsWith('.heic') || name.endsWith('.heif');
}

/** Convert HEIC/HEIF to JPEG. heic2any is heavy (~1 MB) so it is imported only when needed. */
async function convertHeicToJpeg(file) {
  const { default: heic2any } = await import('heic2any');
  const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: PROFILE_PHOTO_JPEG_QUALITY });
  return Array.isArray(converted) ? converted[0] : converted;
}

/**
 * Client-side resize via off-screen canvas. Returns a JPEG Blob that fits
 * within maxDimension x maxDimension. Images already small enough are
 * returned as-is unless they exceed the output byte cap, in which case
 * they are re-encoded as JPEG.
 */
function resizeImageBlob(file, maxDimension, outputMaxBytes) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read image file'));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width <= maxDimension && height <= maxDimension && file.size <= outputMaxBytes) {
          resolve(file);
          return;
        }
        const scale = Math.min(1, maxDimension / width, maxDimension / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Canvas resize failed'))),
          'image/jpeg',
          PROFILE_PHOTO_JPEG_QUALITY,
        );
      };
      img.onerror = () => reject(new Error('Could not load image'));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Normalize a picked image for upload: validate type, HEIC conversion, canvas
 * resize. Size is validated on the processed output, not the raw camera file.
 * @param {File} file
 * @param {{ maxDimension: number, outputMaxBytes: number }} opts
 */
export async function prepareImageForUpload(file, { maxDimension, outputMaxBytes }) {
  if (file.size > RAW_IMAGE_INPUT_MAX_BYTES) {
    throw new Error(`Image is too large (max ${RAW_IMAGE_INPUT_MAX_BYTES / (1024 * 1024)} MB).`);
  }
  let source = file;
  if (isHeicFile(file)) {
    try {
      source = await convertHeicToJpeg(file);
    } catch {
      throw new Error('Could not read this photo format. Try a JPEG or PNG image.');
    }
  } else {
    const type = (file.type || '').toLowerCase();
    if (!PROFILE_PHOTO_ALLOWED.has(type)) {
      throw new Error('Use a JPEG, PNG, GIF, WebP, or HEIC image.');
    }
  }
  const resized = await resizeImageBlob(source, maxDimension, outputMaxBytes);
  if (resized.size > outputMaxBytes) {
    throw new Error(`Image is too large (max ${outputMaxBytes / (1024 * 1024)} MB after processing).`);
  }
  return resized;
}

function resizeProfilePhoto(file) {
  return prepareImageForUpload(file, {
    maxDimension: PROFILE_PHOTO_MAX_DIMENSION,
    outputMaxBytes: PROFILE_PHOTO_MAX_BYTES,
  });
}

export function userProfilePhotoStoragePath(userId, tenantId) {
  return `tenants/${normalizeTenantId(tenantId)}/users/${String(userId || '').trim()}/profile-photo`;
}

export function patientProfilePhotoStoragePath(patientId, tenantId) {
  return `tenants/${normalizeTenantId(tenantId)}/patients/${String(patientId || '').trim()}/profile-photo`;
}

/**
 * Upload a care-app user's profile photo. Returns HTTPS download URL for `users/{uid}.photoUrl`.
 * @param {File} file
 * @param {string} userId - Firebase Auth UID / Firestore users doc id
 * @param {string} tenantId
 */
export async function uploadUserProfilePhoto(file, userId, tenantId) {
  assertStorage();
  if (!file || !userId) throw new Error('File and user id are required.');
  const resized = await resizeProfilePhoto(file);
  const path = userProfilePhotoStoragePath(userId, tenantId);
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, resized, { contentType: resized.type || 'image/jpeg' });
  return getDownloadURL(storageRef);
}

/**
 * Upload a patient's profile photo. Returns HTTPS download URL for `patients/{pid}.photoUrl`.
 * @param {File} file
 * @param {string} patientId - Firestore patients doc id
 * @param {string} tenantId
 */
export async function uploadPatientProfilePhoto(file, patientId, tenantId) {
  assertStorage();
  if (!file || !patientId) throw new Error('File and patient id are required.');
  const resized = await resizeProfilePhoto(file);
  const path = patientProfilePhotoStoragePath(patientId, tenantId);
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, resized, { contentType: resized.type || 'image/jpeg' });
  return getDownloadURL(storageRef);
}

/** Remove a care-app user's profile photo (best-effort). */
export async function deleteUserProfilePhoto(userId, tenantId) {
  if (!storage || !userId) return;
  try {
    await deleteObject(ref(storage, userProfilePhotoStoragePath(userId, tenantId)));
  } catch {
    /* not found or permission */
  }
}

/** Remove a patient's profile photo (best-effort). */
export async function deletePatientProfilePhoto(patientId, tenantId) {
  if (!storage || !patientId) return;
  try {
    await deleteObject(ref(storage, patientProfilePhotoStoragePath(patientId, tenantId)));
  } catch {
    /* not found or permission */
  }
}
